import {useCallback, useEffect, useState} from 'react';

import {safeLocalStorage, useStorageChange} from './localStorage';

/**
 * Force Trace — on-demand Datadog trace capture for production debugging.
 *
 * Datadog APM samples traces probabilistically, so most requests are not
 * recorded. When debugging a production issue you often need the *exact*
 * trace for a specific request. Enabling "force trace" causes the Apollo
 * middleware (see `forceTraceMiddleware` in apollo.ts) to set the header
 * `x-wandb-force-trace: true` on every GraphQL request. The backend
 * recognises this header and tells Datadog to always keep the trace.
 *
 * State lives in localStorage (`force_trace_expires_at`) as a millisecond
 * epoch timestamp. This gives us automatic expiry without a server
 * round-trip and means the setting persists across page reloads. The
 * `useForceTrace` hook provides React state, a countdown label, and
 * cross-tab sync via the `storage` event so all open tabs stay in sync.
 *
 * The toggle is exposed in the profile menu, gated to internal users.
 * It auto-expires after 5 minutes to prevent accidentally leaving it on.
 * Debug users (`@debug.wandb.com`) always force traces while signed in.
 *
 * To find forced traces in Datadog:
 *   env:prod @wandb.force_trace:true
 */
const FORCE_TRACE_KEY = 'force_trace_expires_at';
const FORCE_TRACE_DURATION_MS = 5 * 60 * 1000; // 5 minutes
const DEBUG_VIEWER_EMAIL_SUFFIX = '@debug.wandb.com';

function isDebugViewerEmail(email: string | undefined): boolean {
  return email?.toLowerCase().endsWith(DEBUG_VIEWER_EMAIL_SUFFIX) ?? false;
}

export function getForceTraceExpiresAt(): number | null {
  const raw = safeLocalStorage.getItem(FORCE_TRACE_KEY);
  if (!raw) {
    return null;
  }
  const ts = Number(raw);
  if (Number.isNaN(ts)) {
    return null;
  }
  return Date.now() < ts ? ts : null;
}

export function isForceTraceActive(viewerEmail?: string): boolean {
  return isDebugViewerEmail(viewerEmail) || getForceTraceExpiresAt() !== null;
}

export function enableForceTrace(): void {
  safeLocalStorage.setItem(
    FORCE_TRACE_KEY,
    String(Date.now() + FORCE_TRACE_DURATION_MS)
  );
}

export function disableForceTrace(): void {
  safeLocalStorage.removeItem(FORCE_TRACE_KEY);
}

export function useForceTrace(viewerEmail?: string): {
  isActive: boolean;
  remainingLabel: string | null;
  toggle: () => void;
} {
  const isAlwaysActive = isDebugViewerEmail(viewerEmail);
  const [isActive, setIsActive] = useState(
    () => getForceTraceExpiresAt() !== null
  );
  const [remainingLabel, setRemainingLabel] = useState<string | null>(null);
  // Track expiresAt so cross-tab re-enable (same isActive, new expiry)
  // still restarts the countdown effect.
  const [expiresAt, setExpiresAt] = useState<number | null>(
    getForceTraceExpiresAt
  );

  const toggle = useCallback(() => {
    if (isAlwaysActive) {
      return;
    }
    if (isActive) {
      disableForceTrace();
      setIsActive(false);
      setExpiresAt(null);
      setRemainingLabel(null);
    } else {
      enableForceTrace();
      setIsActive(true);
      setExpiresAt(getForceTraceExpiresAt());
    }
  }, [isActive, isAlwaysActive]);

  const onStorageChange = useCallback(() => {
    const newExpiry = getForceTraceExpiresAt();
    setIsActive(newExpiry !== null);
    setExpiresAt(newExpiry);
    if (!newExpiry) {
      setRemainingLabel(null);
    }
  }, []);

  useStorageChange(FORCE_TRACE_KEY, onStorageChange);

  // Countdown interval — restarts when expiresAt changes (cross-tab
  // re-enable, toggle). The interval tick itself handles expiry detection.
  useEffect(() => {
    if (isAlwaysActive || !isActive || !expiresAt) {
      setRemainingLabel(null);
      return;
    }

    const tick = () => {
      const remaining = expiresAt - Date.now();
      if (remaining <= 0) {
        setIsActive(false);
        setExpiresAt(null);
        setRemainingLabel(null);
        return;
      }
      const mins = Math.floor(remaining / 60000);
      const secs = Math.floor((remaining % 60000) / 1000);
      setRemainingLabel(`${mins}:${String(secs).padStart(2, '0')}`);
    };

    tick();
    const intervalId = setInterval(tick, 1000);

    return () => {
      clearInterval(intervalId);
    };
  }, [isActive, isAlwaysActive, expiresAt]);

  return {
    isActive: isAlwaysActive || isActive,
    remainingLabel: isAlwaysActive ? null : remainingLabel,
    toggle,
  };
}
